import { ref, onUnmounted } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import router from '@/router';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

import { anykrowdApi } from '@/api/anykrowdApi';
import { Order, type PendingPurchaseStatus } from '@/shared/interfaces/order';
import { PaymentStatusEnum, ValuesEnum } from '@/enums/values.enum';
import { TransactionType, TransactionSubtype } from '@/enums/wallet.enum';
import { useCart } from '@/modules/shop/composables/useCart';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import type { TopUpTransactionMetadata } from '@/shared/interfaces/topUp';
import type { Transaction } from '@/shared/interfaces/transaction';
import { useAuthStore } from '@/stores/auth/authStore';
import { useSharedStore } from '@/stores/shared/sharedStore';
import { Currency } from '../interfaces/balance';
import { EventCheckoutReq } from '@/shared/interfaces/event';
import { SelfOrderErrorEnum } from '@/enums/values.enum';
import useStorageService from '@/shared/composables/useStorageService';

// Get order details
const getOrderDetails = async (orderId: string): Promise<Order> => {
	const { data } = await anykrowdApi.get<Order>(`orders/${orderId}/payment-status`, false);
	return data;
};

const getPendingPurchaseStatus = async (pendingPurchaseId: string): Promise<PendingPurchaseStatus> => {
	const { data } = await anykrowdApi.get<PendingPurchaseStatus>(`/wallet/pending-order/${pendingPurchaseId}`, true);
	return data;
};

const _purchaseTickets = async (eventId: number, checkoutData: EventCheckoutReq): Promise<{ success: boolean; redirect_url?: string }> => {
	const { data } = await anykrowdApi.post<{ success: boolean; redirect_url?: string }>(`/events/checkout/${eventId}`, checkoutData, false);
	return data;
};

const _purchaseSelfOrder = async (
	tableSpotId: number,
	options: {
		products: { id: number; quantity: number; comments?: string[] }[];
		total: number;
		top_up_amount?: number;
		disable_payments?: boolean;
		redirectUrl?: string;
		table_number?: string;
	},
): Promise<{
	message?: string;
	code?: string;
	success?: string;
	redirect_url?: string;
	transactions?: any[];
	topup_amount?: number;
	currency?: Currency;
	default_currency?: Currency;
}> => {
	try {
		const { data } = await anykrowdApi.post<{ status: string; message: string; redirect_url?: string }>(`/table-spot/${tableSpotId}/order`, options, false);
		return data;
	} catch (e: any) {
		if (e.response?.data?.error === SelfOrderErrorEnum.INSUFFICIENT_BALANCE) {
			return {
				message: e.response?.data?.message,
				code: e.response?.data?.error,
				redirect_url: '',
				transactions: [],
			};
		} else {
			return {
				message: e.message,
				code: e.code,
				redirect_url: '',
				transactions: [],
			};
		}
	}
};

// useTransaction
const useTransaction = () => {
	// Init
	const { t } = useI18n();
	const route = useRoute();
	const { getItemsAndTotal, clear } = useTransactionStore();
	const transactionStore = useTransactionStore();
	const transaction: Transaction = getItemsAndTotal();
	const { clearCart } = useCart();
	const queryClient = useQueryClient();
	const authStore = useAuthStore();
	const sharedStore = useSharedStore();
	const pending_id = route.query.pending_id;
	const { get, set } = useStorageService();

	const transactionStatus = ref<{
		errorSubTitle: string;
		errorTitle: string;
		finished?: boolean;
		subTitle: string;
		success: boolean;
		totalPrice: number;
		isPolling?: boolean;
		pollingMessage?: string;
	}>({
		errorSubTitle: '',
		errorTitle: '',
		finished: false,
		subTitle: '',
		success: false,
		totalPrice: 0,
		isPolling: false,
		pollingMessage: '',
	});

	// Polling state
	let pollingInterval: NodeJS.Timeout | null = null;

	// Cleanup polling on unmount
	onUnmounted(() => {
		if (pollingInterval) {
			clearInterval(pollingInterval);
		}
	});

	// Start polling for payment status
	const startPolling = (orderId: string) => {
		if (pollingInterval) return; // Already polling

		transactionStatus.value = {
			...transactionStatus.value,
			finished: false,
			isPolling: true,
			pollingMessage: t('transaction.payment_in_progress'),
		};

		pollingInterval = setInterval(async () => {
			try {
				const order = await getOrderDetails(orderId);
				if (order.payment_status !== 'open') {
					// Payment status changed, stop polling and process
					if (pollingInterval) {
						clearInterval(pollingInterval);
						pollingInterval = null;
					}

					const topUpType = mapTopUpType(order.type);
					const topUpMetadata = {
						transactionId: orderId,
						transactionType: topUpType,
						paymentAmount: order.fiat_paid_price,
						paymentStatus: order.payment_status,
					};
					handleTopUpMetadata(topUpMetadata);
				}
			} catch (error) {
				console.error('Error polling payment status:', error);
			}
		}, 2000); // Poll every 2 seconds
	};

	// handleTransaction
	const handleTransaction = (topUpMetadata: TopUpTransactionMetadata | undefined, { subTitle, errorSubTitle, errorTitle }: { subTitle: string; errorSubTitle: string; errorTitle: string }) => {
		// Init
		const { paymentAmount, paymentStatus } = topUpMetadata!;
		// Remove queries
		queryClient.removeQueries({ queryKey: ['rfidWallet'] });

		// Handle payment status
		if (paymentStatus === PaymentStatusEnum.paid || paymentStatus === 'no_latest_topup') {
			return {
				errorSubTitle: '',
				errorTitle: '',
				finished: true,
				subTitle,
				success: true,
				totalPrice: paymentAmount,
				isPolling: false,
				pollingMessage: '',
			};
		} else if (paymentStatus === 'open') {
			// Payment is still processing - show "payment in progress"
			return {
				errorSubTitle: '',
				errorTitle: '',
				finished: false,
				subTitle: '',
				success: false,
				totalPrice: paymentAmount,
				isPolling: true,
				pollingMessage: t('transaction.payment_in_progress'),
			};
		} else {
			return {
				errorSubTitle,
				errorTitle,
				finished: true,
				subTitle: '',
				success: false,
				totalPrice: 0,
				isPolling: false,
				pollingMessage: '',
			};
		}
	};

	const handleTopUpMetadata = (topUpMetadata: any) => {
		// Handle transaction per type
		switch (topUpMetadata.transactionType) {
			case TransactionType.TOP_UP: {
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: transaction.subtype === TransactionSubtype.PACKAGE ? 'top_up.success.subtitle_variation' : 'top_up.success.subtitle',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case TransactionType.VOUCHER_TOP_UP: {
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'top_up.redeem_voucher.voucher_redeemed',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case TransactionType.SHOP_ORDER: {
				topUpMetadata.paymentAmount = transaction.totalPriceNumber;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'shop.order.success.subtitle',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case TransactionType.PAYMENT: {
				topUpMetadata.paymentAmount = transaction.totalPriceNumber;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'ticket.success',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});

				// Update tickets timestamp to force preload
				get(ValuesEnum.StoreCacheTimestamps).then((timestamps) => {
					const tickets = '2000-01-01T20:04:40.000000Z';
					timestamps.tickets = tickets;
					set(ValuesEnum.StoreCacheTimestamps, timestamps);
				});

				break;
			}
			case TransactionType.SELF_ORDER: {
				topUpMetadata.paymentAmount = transaction.totalPriceNumber;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'self_ordering.success.subtitle',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case TransactionType.QUEUE_BOOKING: {
				topUpMetadata.paymentAmount = transaction.totalPriceNumber;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'queue.purchase_successful_subtitle',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case 'QUEUE_UPSELL' as any: {
				topUpMetadata.paymentAmount = transaction.totalPriceNumber;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'queue.purchase_successful_subtitle',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
			case TransactionType.WALLET_MERGE: {
				topUpMetadata.paymentAmount = topUpMetadata.paymentAmountTotal!;
				transactionStatus.value = handleTransaction(topUpMetadata, {
					subTitle: 'rfid.guest.merged_wallet.balance',
					errorSubTitle: 'transaction.error.subtitle',
					errorTitle: 'transaction.error.title',
				});
				break;
			}
		}
	};

	const applyPendingPurchaseStatus = (pendingPurchase: PendingPurchaseStatus): boolean => {
		if (pendingPurchase.status === 'completed') {
			handleTopUpMetadata({
				transactionId: pendingPurchase.created_order?.nanoid || '',
				transactionType: transaction.type,
				paymentAmount: transaction.totalPriceNumber,
				paymentStatus: PaymentStatusEnum.paid,
			});

			return true;
		}

		if (pendingPurchase.status === 'purchase_failed') {
			transactionStatus.value = {
				errorSubTitle: pendingPurchase.wallet_credit_retained ? 'transaction.booking_failed.wallet_credit' : 'transaction.booking_failed.subtitle',
				errorTitle: 'transaction.booking_failed.title',
				finished: true,
				subTitle: '',
				success: false,
				totalPrice: 0,
				isPolling: false,
				pollingMessage: '',
			};

			return true;
		}

		if (pendingPurchase.status === 'top_up_failed') {
			handleTopUpMetadata({
				transactionId: '',
				transactionType: transaction.type,
				paymentAmount: 0,
				paymentStatus: PaymentStatusEnum.failed,
			});

			return true;
		}

		transactionStatus.value = {
			...transactionStatus.value,
			finished: false,
			isPolling: true,
			pollingMessage: t(pendingPurchase.status === 'processing' ? 'transaction.booking_processing' : 'transaction.payment_in_progress'),
		};

		return false;
	};

	const startPendingPurchasePolling = async (pendingPurchaseId: string) => {
		if (pollingInterval) return;

		const checkPendingPurchase = async () => {
			try {
				const pendingPurchase = await getPendingPurchaseStatus(pendingPurchaseId);
				if (applyPendingPurchaseStatus(pendingPurchase) && pollingInterval) {
					clearInterval(pollingInterval);
					pollingInterval = null;
				}
			} catch (error) {
				console.error('Error polling pending purchase:', error);
			}
		};

		await checkPendingPurchase();
		if (!transactionStatus.value.finished) {
			pollingInterval = setInterval(checkPendingPurchase, 2000);
		}
	};

	const normalizeSelfOrderTopUpReturn = (order: Order) => {
		const adminFeeDescription = t('transaction.administrative_fee');
		const productItems = transaction.items.filter((item) => item.additionalInfo?.product);

		if (productItems.length) {
			transaction.items.splice(0, transaction.items.length, ...productItems);
		}

		if (!transaction.items.find((item) => item.description === adminFeeDescription) && +order.app_service_cost > 0) {
			transaction.items.push({
				additionalInfo: {
					id: 'administrative_fee',
					product: {
						currency: {
							name: 'EUR',
							prefix: '€',
						},
					},
				},
				description: adminFeeDescription,
				itemPrice: +order.app_service_cost,
				quantity: 1,
				currency: '€',
			});
		}

		const totalPriceString = transactionStore.calculateTotalsPerProductCurrency();
		const totalPriceNumber = transaction.items.reduce((sum, item) => sum + item.itemPrice, 0);

		transaction.totalPriceString = totalPriceString;
		transaction.totalPriceNumber = +totalPriceNumber.toFixed(2);
		transaction.totalPriceWithoutFee = +(totalPriceNumber - +order.app_service_cost).toFixed(2);
	};

	const mapTopUpType = (orderType: string) => {
		switch (orderType) {
			case 'topup': {
				return TransactionType.TOP_UP;
			}
			default: {
				return TransactionType.TOP_UP;
			}
		}
	};

	// Handle order
	if (route.query && route.query.orderId) {
		const orderId = route.query.orderId!.toString();
		getOrderDetails(orderId).then((order) => {
			const isSelfOrderTopUpReturn = transaction.type === TransactionType.SELF_ORDER && order.type === 'topup';
			const topUpType = isSelfOrderTopUpReturn ? TransactionType.SELF_ORDER : mapTopUpType(order.type);
			const topUpMetadata = {
				transactionId: orderId,
				transactionType: topUpType,
				paymentAmount: order.fiat_paid_price,
				paymentStatus: order.payment_status,
			};

			if (isSelfOrderTopUpReturn) {
				normalizeSelfOrderTopUpReturn(order);
			} else if (!transaction.items.find((item) => item.description === t('transaction.administrative_fee'))) {
				if (+order.app_service_cost > 0) {
					transaction.items.push({
						description: t('transaction.administrative_fee'),
						itemPrice: +order.app_service_cost,
						quantity: 1,
					});

					const totalPriceString = transactionStore.calculateTotalsPerProductCurrency();
					transaction.totalPriceString = totalPriceString;
				}
			}

			if (pending_id) {
				startPendingPurchasePolling(pending_id.toString());

				return;
			}

			// Check if payment status is 'open' and start polling if needed
			if (order.payment_status === 'open') {
				startPolling(orderId);
			}

			handleTopUpMetadata(topUpMetadata);
		});
	} else if (pending_id) {
		startPendingPurchasePolling(pending_id.toString());
	} else {
		const topUpMetadata = {
			transactionId: '',
			transactionType: transaction.type,
			paymentAmount: transaction.totalPriceNumber,
			paymentStatus: 'paid',
		};
		handleTopUpMetadata(topUpMetadata);
	}

	// goTo
	const goTo = async () => {
		const isAuthenticated = await authStore.isAuthenticated();
		let redirectPath = '';
		const transactionSucceeded = transactionStatus.value.success;
		const shouldRefreshWallets = transactionSucceeded && transaction.type === TransactionType.VOUCHER_TOP_UP;

		if (transaction.type === TransactionType.SELF_ORDER && !isAuthenticated) {
			redirectPath = `/dashboard?spotid=${sharedStore.currentSelfOrderingSpotId}`;
		} else {
			redirectPath = transaction.additionalInfo && transaction.additionalInfo.redirectPath ? transaction.additionalInfo.redirectPath : '/dashboard';
		}

		if (transactionSucceeded) {
			if (shouldRefreshWallets) {
				queryClient.removeQueries({ queryKey: ['wallets'] });
			}

			clearCart();
			clear();
			useTransactionStore().$reset();
		}
		await router.push(redirectPath);

		if (shouldRefreshWallets) {
			await queryClient.invalidateQueries({ queryKey: ['wallets'] });
		}
	};

	// Return
	return {
		goTo,
		transaction,
		transactionStatus,
	};
};

export default useTransaction;
